import { Matrix4 } from '@math.gl/core'
import { Position } from 'types'
import { ArtboardFilter } from '../types'
import Attribute from './attribute'
import Uniform from './uniform'
import { createShaderProgram, shaderSourceFromFilter } from './utilities'

const QUAD = [0, 0, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1]

export type ShaderSource = {
    fragment: string
    vertex: string
}

export interface DrawImageProps extends Position {
    mirrored?: boolean
    texture: WebGLTexture
    clipMatrix?: Matrix4
}

class Shader {
    private gl: WebGLRenderingContext
    private program: WebGLProgram
    private _intensity: number = 1
    private _progress: number = 0

    readonly attributes: {
        position: Attribute
        textureCoordinates: Attribute
    }

    readonly buffers: {
        position: WebGLBuffer
        textureCoordinates: WebGLBuffer
    }

    readonly uniforms: {
        intensity: Uniform
        matrix: Uniform
        progress: Uniform
        texture: Uniform
        textureMatrix: Uniform
    }

    constructor(gl: WebGLRenderingContext, filter?: ArtboardFilter) {
        const source = shaderSourceFromFilter(filter)
        const program = createShaderProgram(gl, source.fragment, source.vertex)

        this.gl = gl
        this.program = program

        this.attributes = {
            position: new Attribute(gl, program, 'a_position', 2),
            textureCoordinates: new Attribute(gl, program, 'a_texcoord', 2),
        }

        this.buffers = {
            position: gl.createBuffer()!,
            textureCoordinates: gl.createBuffer()!,
        }

        this.uniforms = {
            intensity: new Uniform(gl, program, 'u_intensity', 'float'),
            matrix: new Uniform(gl, program, 'u_matrix', 'matrix4'),
            progress: new Uniform(gl, program, 'u_progress', 'float'),
            texture: new Uniform(gl, program, 'u_texture', 'integer'),
            textureMatrix: new Uniform(gl, program, 'u_texmatrix', 'matrix4'),
        }

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position)
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(QUAD), gl.STATIC_DRAW)

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.textureCoordinates)
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(QUAD), gl.STATIC_DRAW)
    }

    delete() {
        const { gl } = this

        gl.deleteBuffer(this.buffers.position)
        gl.deleteBuffer(this.buffers.textureCoordinates)
        gl.deleteProgram(this.program)
    }

    drawImage({
        texture,
        x,
        y,
        width,
        height,
        mirrored = false,
        rotation = 0,
        clipMatrix = new Matrix4(),
    }: DrawImageProps) {
        const { attributes, buffers, gl, uniforms } = this
        const matrix = new Matrix4()

        gl.bindTexture(gl.TEXTURE_2D, texture)

        gl.useProgram(this.program)

        attributes.position.upload(buffers.position)
        attributes.textureCoordinates.upload(buffers.textureCoordinates)

        matrix.ortho({
            bottom: gl.canvas.height,
            far: -1,
            left: 0,
            near: 0,
            right: gl.canvas.width,
            top: 0,
        })

        matrix.translate([x, y, 0])

        if (rotation) {
            matrix.translate([width / 2, height / 2, 0])
            matrix.rotateZ(rotation * (Math.PI / 180))
            matrix.translate([-width / 2, -height / 2, 0])
        }

        if (mirrored) {
            matrix.translate([width, 0, 0])
            matrix.scale([-width, height, 1])
        } else {
            matrix.scale([width, height, 1])
        }

        uniforms.intensity.upload(Math.max(0, Math.min(1, this._intensity)))
        uniforms.matrix.upload(matrix)
        uniforms.progress.upload(this._progress)
        uniforms.texture.upload(0)
        uniforms.textureMatrix.upload(clipMatrix)

        gl.drawArrays(gl.TRIANGLES, 0, 6)
    }

    get intensity() {
        return this._intensity
    }

    get progress() {
        return this._progress
    }

    setIntensity(intensity: number) {
        this._intensity = intensity
    }

    setProgress(progress: number) {
        this._progress = progress
    }
}

export default Shader
